import { useEffect } from 'react';
import Router from './Router';
import { BrowserRouter } from 'react-router-dom';
import { useGetEOSEnums, useGetUserFeatureFlags } from '@api';
import {
	authenticatedUser,
	FeatureFlags,
	EOSENUMS,
	GoogleMapsLoaded,
	JWT,
} from '@state';
import { useSetRecoilState, useRecoilState, useRecoilValue } from 'recoil';
import { Toaster } from 'react-hot-toast';
import { ToastUI, FlagsProvider, PageTitleProvider } from '@components';
import { localFeatureFlags } from '../FeatureFlags.local';
import { useJsApiLoader, Libraries } from '@react-google-maps/api';
import { injectClarity, injectPendo, waitForPendoLoad } from '@utils';
import usePendoIdentity from '@hooks/usePendoIdentity.ts';
import { useIsLocal } from '@hooks';

const mapsLibraries: Libraries = ['maps', 'marker', 'places', 'geometry', 'drawing'];

const Injector = () => {
	const isLocal = useIsLocal();
	injectPendo('2299936e-93a6-4810-5cab-4965afb38241');
	if (!isLocal) injectClarity();
	usePendoIdentity();
	const configurePendo = () => {
		/* Initial configuration of Pendo
		   Once logged in, the pendo identity will be updated */
		(window as any).pendo.initialize({ visitor: { id: 'VISITOR-UNIQUE-ID' } });
	}
	waitForPendoLoad(() => {
		configurePendo();
	});

	const jwt = useRecoilValue(JWT);
	const authUser = useRecoilValue(authenticatedUser);
	const userExternalId = authUser?.user_id;
	const { callGetEOSEnums } = useGetEOSEnums();
	const setEosEnums = useSetRecoilState(EOSENUMS);
	const { callGetFlags } = useGetUserFeatureFlags();
	const [flags, setFeatureFlags] = useRecoilState(FeatureFlags);
	const setGoogleMapsLoaded = useSetRecoilState(GoogleMapsLoaded);
	const { isLoaded } = useJsApiLoader({
		id: 'google-map-script',
		libraries: mapsLibraries,
		googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS || ''
	});

	useEffect(() => {
		if (!userExternalId) return;
		callGetFlags().then((res) => {
			if (res && res.data) {
				const flagsFromServer = res.data.data.flags;
				const flags = flagsFromServer.map((flag) => {
					return {
						name: flag.id,
						on: flag.active,
						meta: flag
					}
				});
				setFeatureFlags(flags);
			}
		}).catch((err) => {
			if (err && err.cause) console.error(err);
		});
	}, [userExternalId]);
	useEffect(() => {
		callGetEOSEnums().then((res) => {
			if (res) setEosEnums(res.data.data);
		}).catch((err) => {
			if (err && err.cause) console.error(err);
		});
	}, [jwt]);
	useEffect(() => {
		setGoogleMapsLoaded(isLoaded);
	}, [isLoaded]);

	return (
		<>
			<FlagsProvider flags={[
				...(isLocal ? localFeatureFlags : []),
				...flags
			]}>
				<PageTitleProvider>
					<BrowserRouter future={{ v7_startTransition: true }}>
						<Toaster containerStyle={{ zIndex: 9999999999 }} />
						<ToastUI />
						<Router />
					</BrowserRouter>
				</PageTitleProvider>
			</FlagsProvider>
		</>
	);
}

export default Injector;
